import { reportingClient } from "./httpClients/reportingClient";

const UPLOAD_PATH = "/api/ImageAnalysis/Upload";
const ANALYZE_PATH = "/api/ImageAnalysis/Analyze";
const FOLLOWUP_PATH = "/api/ImageAnalysis/FollowUp";
const CONTEXT_QUESTIONS_PATH = "/api/ImageAnalysis/ContextQuestions";

function parseEnvelope(data) {
  const raw = data?.data ?? data ?? {};
  const ok = raw.success ?? raw.Success;
  if (!ok) {
    const info = raw.errorInfo ?? raw.ErrorInfo ?? {};
    const msg = info.message ?? info.Message ?? "Request failed";
    const err = new Error(msg);
    err.code = info.code ?? info.Code;
    throw err;
  }
  return raw.payload ?? raw.Payload ?? null;
}

/**
 * Step 1 — Upload
 * Form field name: "File"
 */
export async function uploadImageAnalysisFile(file) {
  if (!file) throw new Error("File is required");

  const formData = new FormData();
  formData.append("File", file);

  const response = await reportingClient.post(UPLOAD_PATH, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  const payload = parseEnvelope(response);
  return {
    fileId: payload?.fileId ?? payload?.FileId,
    originalFileName:
      payload?.originalFileName ?? payload?.OriginalFileName ?? file.name,
    fileSizeBytes: payload?.fileSizeBytes ?? payload?.FileSizeBytes,
  };
}

/**
 * Step 2 — Analyze
 */
export async function analyzeImageAnalysis({
  uploadedImageFileId,
  studyId,
  organisationId,
  userInputText,
  language,
  modelSpeed,
  addAnnotations = false,
}) {
  const body = {
    uploadedImageFileId,
    studyId,
    userInputText,
    language,
    modelSpeed,
    addAnnotations: Boolean(addAnnotations),
  };
  // Backend expects organisationId in sample. For Radical it may be optional.
  if (organisationId) body.organisationId = organisationId;

  const response = await reportingClient.post(ANALYZE_PATH, body);
  const payload = parseEnvelope(response);
  const findingsImageUrl =
    payload?.findingsImageUrl ?? payload?.FindingsImageUrl ?? null;
  return {
    answer: payload?.answer ?? payload?.Answer ?? "",
    rawAnswer: payload?.rawAnswer ?? payload?.RawAnswer ?? "",
    findingsImageUrl:
      typeof findingsImageUrl === "string" && findingsImageUrl.trim()
        ? findingsImageUrl.trim()
        : null,
    totalUsedTokens: payload?.totalUsedTokens ?? payload?.TotalUsedTokens,
  };
}

/**
 * Step 3/4 — FollowUp
 */
export async function followUpImageAnalysis({
  uploadedImageFileId,
  studyId,
  organisationId,
  userInputText,
  language,
  modelSpeed,
  question,
  conversationHistory,
}) {
  const body = {
    uploadedImageFileId,
    studyId,
    userInputText,
    language,
    modelSpeed,
    question,
    conversationHistory: conversationHistory || [],
  };
  if (organisationId) body.organisationId = organisationId;

  const response = await reportingClient.post(FOLLOWUP_PATH, body);
  const payload = parseEnvelope(response);

  // Updated backend contract (Vigen 2026-06-02):
  // FollowUp payload includes `contextQuestions` along with answer/rawAnswer.
  const questions = payload?.contextQuestions ?? [];

  return {
    answer: payload?.answer ?? payload?.Answer ?? "",
    rawAnswer: payload?.rawAnswer ?? payload?.RawAnswer ?? "",
    totalUsedTokens: payload?.totalUsedTokens ?? payload?.TotalUsedTokens,
    questions: Array.isArray(questions) ? questions : [],
  };
}

/**
 * Step 5 — ContextQuestions (same body as FollowUp)
 */
export async function getImageAnalysisContextQuestions({
  uploadedImageFileId,
  studyId,
  organisationId,
  userInputText,
  language,
  modelSpeed,
  question,
  conversationHistory,
}) {
  const body = {
    uploadedImageFileId,
    studyId,
    userInputText,
    language,
    modelSpeed,
    question,
    conversationHistory: conversationHistory || [],
  };
  if (organisationId) body.organisationId = organisationId;

  const response = await reportingClient.post(CONTEXT_QUESTIONS_PATH, body);
  const payload = parseEnvelope(response);
  return {
    questions: payload?.questions ?? payload?.Questions ?? [],
    totalUsedTokens: payload?.totalUsedTokens ?? payload?.TotalUsedTokens,
  };
}

